import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { saveCustomerInfo, updateCustomerInfo } from '@services/checkoutService';
import { useCheckoutStore } from '@store/checkoutStore';
import { useTenantConfigContext } from '@providers/TenantConfigProvider';
import type { CustomerInfoFormValues, CustomerInfoRequest, CustomerInfoResponse } from '@t/checkout.types';
import { stripCountryCode } from '@utils/phoneNumberValidation/phoneNumberValidation';
import { sanitizeNameInput } from '@utils/nameInput';

interface UseSaveCustomerInfoParams {
  sessionId: string | null;
  resolvedAddressId: string | null;
  savedCustomerInfo: CustomerInfoFormValues | null;
  onSuccess?: (values: CustomerInfoFormValues) => void;
}

const buildPayload = (
  values: CustomerInfoFormValues,
  resolvedAddressId: string | null,
  tmfEnabled: boolean
): CustomerInfoRequest => {
  const howDidYouHearAboutUs =
    values.howDidYouHearAboutUs === 'Other' ? values.howDidYouHearAboutUsOther : values.howDidYouHearAboutUs;

  return {
    resolvedAddressId: resolvedAddressId ?? undefined,
    titleId: values.titleId ? Number(values.titleId) : undefined,
    firstName: sanitizeNameInput(values.firstName),
    middleName: values.middleName ? sanitizeNameInput(values.middleName) : undefined,
    lastName: sanitizeNameInput(values.lastName),
    email: values.email,
    dateOfBirth: values.dateOfBirth,
    howDidYouHearAboutUs: howDidYouHearAboutUs || undefined,
    primaryPhoneNumber: stripCountryCode(values.primaryPhoneNumber),
    primaryPhoneNumberType: values.primaryPhoneNumberType || undefined,
    // TMF tenants don't offer the MyNovus account/password step — never send one.
    password: tmfEnabled ? '' : values.password,
    billDelivery: values.billDelivery,
    billingAddress: values.useSameAddress
      ? undefined
      : {
          street: values.billingStreet,
          unit: values.billingUnit || undefined,
          city: values.billingCity,
          province: values.billingProvince,
          postalCode: values.billingPostalCode,
          country: values.billingCountry,
        },
  };
};

export const useSaveCustomerInfo = ({
  sessionId,
  resolvedAddressId,
  savedCustomerInfo,
  onSuccess,
}: UseSaveCustomerInfoParams) => {
  const queryClient = useQueryClient();
  const setCustomerInfo = useCheckoutStore((s) => s.setCustomerInfo);
  const completeStep = useCheckoutStore((s) => s.completeStep);
  const { tmfEnabled } = useTenantConfigContext();

  const mutation = useMutation<CustomerInfoResponse, Error, CustomerInfoFormValues>({
    mutationFn: (values) => {
      const payload = buildPayload(values, resolvedAddressId, tmfEnabled);
      return savedCustomerInfo ? updateCustomerInfo(sessionId!, payload) : saveCustomerInfo(sessionId!, payload);
    },
    onSuccess: (_, values) => {
      setCustomerInfo(values);
      queryClient.invalidateQueries({ queryKey: ['installationDetails', sessionId] });
      completeStep('customer');
      onSuccess?.(values);
    },
    onError: () => {
      toast.error('Failed to save customer information. Please try again.');
    },
  });

  return {
    save: mutation.mutate,
    isSaving: mutation.isPending,
  };
};
